import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowDown01Icon,
  ArrowUp01Icon
} from "@hugeicons-pro/core-stroke-rounded";
import {
  IconButton,
  IconButtonSize,
  IconButtonVariant
} from "shared/components";
import { track } from "shared/telemetry/track";
import { useApplicationNeighbors } from "views/applications-id/hooks/use-application-neighbors";
import { useApplicationNavigationEnabled } from "views/applications-id/hooks/use-application-navigation-flag";

type Direction = "next" | "previous";

const KEY_DIRECTIONS: Record<string, Direction> = {
  arrowdown: "next",
  j: "next",
  arrowup: "previous",
  k: "previous"
};

// Anything open on top of the page: modals, popovers and the chat pop-up are
// role="dialog"; react-modern-drawer drawers (Notes, Activity log, Configure
// metrics...) render none, only a checkbox that is checked while open.
const OVERLAY_OPEN = '[role="dialog"], .EZDrawer__checkbox:checked';

// Shortcuts stay out of the way of typing, browser/OS shortcuts, held-down
// keys, keys another handler already took and anything open on the page.
const getShortcutDirection = (event: KeyboardEvent): Direction | undefined => {
  if (event.defaultPrevented || event.repeat) return;
  if (event.metaKey || event.ctrlKey || event.altKey) return;
  if (event.shiftKey) return;
  const target = event.target as Element | null;
  if (
    target?.closest?.(
      'input, textarea, select, [contenteditable]:not([contenteditable="false"])'
    )
  )
    return;
  if (document.querySelector(OVERLAY_OPEN)) return;
  return KEY_DIRECTIONS[event.key?.toLowerCase()];
};

const Navigator = ({ applicationId }: { applicationId: string }) => {
  const navigate = useNavigate();
  const { data } = useApplicationNeighbors(applicationId);
  // The list the neighbors came from, not the one in router state: after a
  // fallback the walk stays on the default list it shows.
  const listQuery = data?.listQuery;
  const previousId = data?.previous_id ?? null;
  const nextId = data?.next_id ?? null;

  const go = (direction: Direction, source: "button" | "keyboard") => {
    const id = direction === "next" ? nextId : previousId;
    if (id === null) return false;
    track("application.neighbor.selected", {
      application_id: applicationId,
      direction,
      source
    });
    navigate(
      `/applications/${id}`,
      listQuery ? { state: { applicationsListQuery: listQuery } } : undefined
    );
    return true;
  };

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const direction = getShortcutDirection(event);
      if (direction && go(direction, "keyboard")) event.preventDefault();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [applicationId, previousId, nextId, listQuery]);

  if (!data?.position) return null;

  return (
    <div className="flex items-center gap-1">
      <span className="text-sm font-medium text-content-500 whitespace-nowrap">
        {`${data.position} of ${data.total_count}`}
      </span>
      <IconButton
        aria-label="Previous application"
        HugeIcon={ArrowUp01Icon}
        variant={IconButtonVariant.BASE}
        size={IconButtonSize.XS}
        borderLess
        disabled={previousId === null}
        tooltip="Previous application (↑ or K)"
        onClick={() => go("previous", "button")}
      />
      <IconButton
        aria-label="Next application"
        HugeIcon={ArrowDown01Icon}
        variant={IconButtonVariant.BASE}
        size={IconButtonSize.XS}
        borderLess
        disabled={nextId === null}
        tooltip="Next application (↓ or J)"
        onClick={() => go("next", "button")}
      />
    </div>
  );
};

// APPLICATION NAVIGATION FLAG: with the flag off nothing mounts, so there is
// no /neighbors request and no keydown listener.
export const ApplicationNavigator = ({
  applicationId
}: {
  applicationId: string;
}) =>
  useApplicationNavigationEnabled() ? (
    <Navigator applicationId={applicationId} />
  ) : null;
